import React from "react";
import { AlertTriangle, RotateCw, X, Loader2, ScanSearch } from "lucide-react";
import { liveStatus } from "@/lib/posting";

// Right-side "Streaming in" panel — upload & forget. Users can open it to watch
// live processing status for every uploaded doc, or ignore it entirely.
export function StreamingPanel({ open, onClose, uploads = [], checks = [], procMap = {}, onRetry, onDismiss }) {
  if (!open) return null;
  const count = uploads.length + checks.length;
  return (
    <>
      <div className="pp-sp-backdrop" onClick={onClose} data-testid="pp-sp-backdrop" />
      <aside className="pp-sp" data-testid="pp-streaming-panel" role="dialog" aria-label="Streaming in">
        <div className="pp-sp-head">
          <div>
            <div className="pp-t">Streaming in <span className="pp-sn">{count}</span></div>
            <div className="pp-sp-hint">Lens processes these in the background — you can keep working.</div>
          </div>
          <button className="pp-sp-close" onClick={onClose} data-testid="pp-sp-close" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="pp-sp-body">
          {count === 0 ? (
            <div className="pp-sp-empty">Nothing streaming in right now. Uploaded documents will appear here with live status.</div>
          ) : (
            <>
              {uploads.map((u) => <UploadItem key={u.tempId} u={u} onRetry={onRetry} onDismiss={onDismiss} />)}
              {checks.map((c) => <ProcessingItem key={c.id} c={c} proc={procMap[c.id] || c.proc} />)}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function UploadItem({ u, onRetry, onDismiss }) {
  const err = u.state === "error";
  return (
    <div className="pp-sp-item" data-testid={`pp-sp-up-${u.tempId}`}>
      <div className="pp-sp-row">
        <div className={`pp-sp-ic ${err ? "pp-err" : ""}`}>
          {err ? <AlertTriangle size={15} /> : <Loader2 size={15} className="animate-spin" />}
        </div>
        <div className="pp-sp-b">
          <div className="pp-sp-name">
            <span className="pp-nm">{u.name}</span>
            <span className={`pp-lenschip ${err ? "pp-err" : ""}`}>{err ? "FAILED" : u.state === "processing" ? "LENS · READING" : "UPLOADING"}</span>
          </div>
          <div className={`pp-sp-msg ${err ? "pp-err" : ""}`}>
            {err ? (u.error || "Upload failed") : u.state === "processing" ? "Uploaded · Lens is extracting the remittance…" : `Uploading… ${u.pct}%`}
          </div>
          {!err && u.state === "uploading" ? (
            <div className="pp-uptrack" style={{ marginTop: 8, marginLeft: 0, width: "100%" }}>
              <div className="pp-upfill-lens" style={{ width: `${u.pct}%` }} />
            </div>
          ) : null}
          {err ? (
            <div className="pp-sp-actions">
              <button className="pp-pbtn pp-manage" onClick={() => onRetry?.(u)} disabled={!u.file} data-testid={`pp-sp-retry-${u.tempId}`}>
                <RotateCw size={12} style={{ marginRight: 5 }} /> Retry
              </button>
              <button className="pp-pbtn pp-history" onClick={() => onDismiss?.(u.tempId)} data-testid={`pp-sp-dismiss-${u.tempId}`}>Dismiss</button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProcessingItem({ c, proc }) {
  const live = liveStatus(proc || c.proc);
  return (
    <div className="pp-sp-item" data-testid={`pp-sp-check-${c.id}`}>
      <div className="pp-sp-row">
        <div className="pp-sp-ic"><ScanSearch size={15} /></div>
        <div className="pp-sp-b">
          <div className="pp-sp-name">
            <span className="pp-nm">{c.payer !== "—" ? c.payer : c.filename}</span>
            <span className="pp-lenschip" data-testid={`pp-sp-stage-${c.id}`}>✦ {live?.label || "READING"}</span>
          </div>
          <div className="pp-sp-msg">{live?.sub || "Lens is matching lines to claims & decoding CARC codes…"}</div>
        </div>
      </div>
    </div>
  );
}
